import _ from 'underscore';
import React, { useCallback, useContext, useMemo } from 'react';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import {
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  mergeClasses,
} from '@fluentui/react-components';
import { useFormService, FORM_STATES, FORM_EVENTS } from '@bingads-webui-react/form-core';

interface IDialogConfig {
  disableConfirmPopup?: boolean,
  className?: string,
  title?: string,
  content?: string,
  confirmText?: string,
  abortText?: string,
  onConfirmFn?: () => void,
  onCancelFn?: () => void,
}

interface CancelDialogProps {
  config?: IDialogConfig | ((stateMatch?) => any);
}

export const CancelDialog: React.FC<CancelDialogProps> = (props) => {
  const i18n = useContext(I18nContext) as any;
  const { state, send } = useFormService();
  const showDialog = state.matches(FORM_STATES.CANCELING);
  const useConfig = useMemo(() => (_.isFunction(props.config) ? props.config : () => props.config), [props.config]);

  const {
    className = null,
    title = null,
    content = null,
    confirmText = null,
    abortText = null,
    onConfirmFn = null,
    onCancelFn = null,
  } = useConfig(showDialog) || {};

  const onConfirm = useCallback(() => {
    if (onConfirmFn) {
      onConfirmFn();
    }
    send(FORM_EVENTS.CONFIRM_CANCEL);
  }, [onConfirmFn, send]);

  const onCancel = useCallback(() => {
    if (onCancelFn) {
      onCancelFn();
    }
    send(FORM_EVENTS.ABORT_CANCEL);
  }, [onCancelFn, send]);

  return (
    <Dialog open={showDialog}>
      <DialogSurface className={mergeClasses('form-cancel-dialog', className)}>
        <DialogBody>
          <DialogTitle>{title || i18n.getString(_TL_('Are you sure you want to exit?'))}</DialogTitle>
          <DialogContent>
            {content || i18n.getString(_TL_('You have unsaved changes that could be lost.'))}
          </DialogContent>
          <DialogActions>
            <Button
              appearance="primary"
              className="dialog-confirm"
              onClick={onConfirm}
            >
              {confirmText || i18n.getString(_TL_('Yes'))}
            </Button>
            <Button
              appearance="secondary"
              className="dialog-cancel"
              onClick={onCancel}
            >
              {abortText || i18n.getString(_TL_('No'))}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};
